import React, { useState, useMemo, useEffect } from "react";
import { withTranslation } from "react-i18next";
import {
  Autocomplete,
  TextField,
  Button,
  Grid,
  useMediaQuery,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import PropTypes from "prop-types";

import InputFieldText from "Components/Common/InputFieldText";

import { useGetApplicantStatusQuery } from "apis/FireSafety";

const initialFilters = {
  selectedStatusId: "",
  selectedStartDate: null,
  selectedEndDate: null,
  buildingName: null,
  fscaNumber: null,
};

const FilterFireSafetyApplications = ({
  setQuery,
  resetPage = () => {},
  isBuildingObservationSideBarSelected,
  t,
}) => {
  const { data: { data: status = [] } = {} } = useGetApplicantStatusQuery();

  const isExtraLargeScreen = useMediaQuery("(max-width: 1620px)");

  const [
    {
      selectedStatusId,
      selectedStartDate,
      selectedEndDate,
      buildingName,
      fscaNumber,
    },
    setSelectedFilteredOption,
  ] = useState(initialFilters);

  const [invalidDateMessage, setInvalidDateMessage] = useState({
    startDateErrorMessage: "",
    endDateErrorMessage: "",
  });

  const updatedStatusOptions = useMemo(() => [
    { label: "All", value: "" },
    ...(status?.map(({ id, name }) => ({ label: name, value: id })) ?? []),
  ]);

  const clearBtnHandler = () => {
    resetPage();
    setSelectedFilteredOption(initialFilters);
    setQuery({});
  };

  const searchBtnHandler = (e) => {
    e.preventDefault();
    resetPage();
    setQuery(() => ({
      searchStatus: selectedStatusId,
      fromDate: selectedStartDate?.isValid()
        ? selectedStartDate.format("MM-DD-YYYY")
        : "",
      toDate: selectedEndDate?.isValid()
        ? selectedEndDate.format("MM-DD-YYYY")
        : "",
      buildingName,
      fscaNumber,
    }));
  };

  const errorMessage = (error) => {
    switch (error) {
      case "maxDate":
        return t("Please select date that is before end date");
      case "minDate":
        return t("Please select date that is after start date");
      case "disableFuture":
        return t("You can't select future date");
      case "invalidDate":
        return t("Please enter a valid date");
      case "min1900":
        return t("Select a date that is subsequent than 01/01/1900");
      default:
        return "";
    }
  };

  useEffect(() => clearBtnHandler(), [isBuildingObservationSideBarSelected]);

  return (
    <>
      <form onSubmit={(e) => searchBtnHandler(e)}>
        <Grid container columnSpacing={2} className="mt-2">
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            xl={isExtraLargeScreen ? 3 : 2}
          >
            <Autocomplete
              className="w-100 mb-2"
              size="small"
              options={updatedStatusOptions}
              value={
                updatedStatusOptions.find(
                  (status) => status.value === selectedStatusId
                ) || ""
              }
              disableClearable
              renderInput={(params) => (
                <TextField {...params} label={t("Select Status")} />
              )}
              onChange={(_, option) =>
                setSelectedFilteredOption((prevState) => ({
                  ...prevState,
                  selectedStatusId: option.value,
                }))
              }
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.value}>
                    {option.label}
                  </li>
                );
              }}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            xl={isExtraLargeScreen ? 3 : 2}
          >
            <DatePicker
              label={t("Start Date")}
              className="w-100 mb-2"
              format="DD-MM-YYYY"
              value={selectedStartDate ?? null}
              disableFuture
              maxDate={selectedEndDate}
              onError={(error) =>
                setInvalidDateMessage((prevState) => ({
                  ...prevState,
                  startDateErrorMessage:
                    error === "minDate" ? "min1900" : error,
                }))
              }
              slotProps={{
                textField: {
                  size: "small",
                  helperText: errorMessage(
                    invalidDateMessage.startDateErrorMessage
                  ),
                },
              }}
              onChange={(date) =>
                setSelectedFilteredOption((prevState) => ({
                  ...prevState,
                  selectedStartDate: date,
                }))
              }
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            xl={isExtraLargeScreen ? 3 : 2}
          >
            <InputFieldText
              label={t("Building Name")}
              className="w-100"
              value={buildingName ?? ""}
              placeholder={t("Enter Building Name")}
              onChange={(e) =>
                setSelectedFilteredOption((prevState) => ({
                  ...prevState,
                  buildingName: e.target.value,
                }))
              }
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            xl={isExtraLargeScreen ? 3 : 2}
          >
            <InputFieldText
              label={t("FSCA No.")}
              className="w-100"
              value={fscaNumber ?? ""}
              placeholder={t("Enter FSCA Number")}
              onChange={(e) =>
                setSelectedFilteredOption((prevState) => ({
                  ...prevState,
                  fscaNumber: e.target.value,
                }))
              }
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            xl={isExtraLargeScreen ? 3 : 2}
          >
            <DatePicker
              label={t("End Date")}
              className="w-100 mb-2"
              format="DD-MM-YYYY"
              value={selectedEndDate ?? null}
              disableFuture
              onError={(error) => {
                setInvalidDateMessage((prevState) => ({
                  ...prevState,
                  endDateErrorMessage: error,
                }));
              }}
              minDate={selectedStartDate}
              slotProps={{
                textField: {
                  size: "small",
                  helperText: errorMessage(
                    invalidDateMessage.endDateErrorMessage
                  ),
                },
              }}
              onChange={(date) =>
                setSelectedFilteredOption((prevState) => ({
                  ...prevState,
                  selectedEndDate: date,
                }))
              }
            />
          </Grid>

          <Grid item>
            <Button
              variant="outlined"
              type="submit"
              className="me-3 mb-2"
              disabled={
                !!(
                  invalidDateMessage.startDateErrorMessage ||
                  invalidDateMessage.endDateErrorMessage
                )
              }
            >
              {t("Search")}
            </Button>
            <Button
              variant="outlined"
              onClick={clearBtnHandler}
              className="me-3 mb-2"
            >
              {t("Clear")}
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

FilterFireSafetyApplications.propTypes = {
  setQuery: PropTypes.func,
  resetPage: PropTypes.func,
  isBuildingObservationSideBarSelected: PropTypes.bool,
  t: PropTypes.func,
};

export default withTranslation()(FilterFireSafetyApplications);
